import { useQueryClient } from "@tanstack/react-query";
import FinishBillingProcessOfProjectInvoiceForm
	from "modules/billing/components/DraftInvoicesIndexView/components/DraftProjectInvoiceDetailsSidebar/components/FinishBillingProcessOfProjectInvoiceForm";
import RevertDraftProjectInvoiceStatusForm
	from "modules/billing/components/DraftInvoicesIndexView/components/DraftProjectInvoiceDetailsSidebar/components/RevertDraftProjectInvoiceStatusForm";
import { useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import Sidebar from "~/components/Sidebar";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import { INVOICE_DETAILS_PATH } from "~/constants/appRoutes.ts";
import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import {
	ALL_DRAFT_INVOICES_QUERY_KEY,
	useAllDraftInvoices,
} from "~/modules/billing/api/draftInvoice/draftInvoiceQueries.ts";
import { All_PENDING_INVOICES_QUERY_KEY } from "~/modules/billing/api/pendingInvoice/pendingInvoiceQueries.ts";
import {
	ALL_PROCESSED_INVOICES_QUERY_KEY,
	PROJECTS_INVOICES_QUERY_KEY,
} from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import DraftProjectInvoiceDetails
	from "~/modules/billing/components/DraftInvoicesIndexView/components/DraftProjectInvoiceDetailsSidebar/components/DraftProjectInvoiceDetails";
import { createUnprocessedInvoiceTableDataFromProjectInvoice } from "~/modules/billing/utils/billingUtils.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	invoiceId?: string;
	userCanManageInvoices:boolean;
};

const DraftProjectInvoiceDetailsSidebar = ({
	isOpen,
	onClose,
	invoiceId,
	userCanManageInvoices
}: Props) => {
	const [showFinishBillingProcessForm, setShowFinishBillingProcessForm] = useState<boolean>(false);
	const [showRevertStatusForm, setShowRevertStatusForm] = useState<boolean>(false);
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const handleClose = useCallback(() => {
		onClose();
		setShowFinishBillingProcessForm(false);
		setShowRevertStatusForm(false);
	}, [onClose]);


	const { data, options } = useAggregatedDataV2({
		allUsers: useAllUsers(),
		allDraftInvoices: useAllDraftInvoices(),
	});

	const {
		allDraftInvoices, allUsers,
	} = data;

	const enrichedInvoiceData = useMemo(() => {
		if (allDraftInvoices) {
			const invoice = allDraftInvoices.projectInvoices.find((inv) => inv.id === invoiceId);
			if (invoice) {
				return createUnprocessedInvoiceTableDataFromProjectInvoice(invoice, allDraftInvoices.projectPhases, allDraftInvoices.projects);
			}
		}
		return null;
	}, [allDraftInvoices, invoiceId]);

	const isProjectInvoice = enrichedInvoiceData?.isProjectInvoice;

	const handleFinishSuccess = useCallback(async (newInvoiceId: string) => {
		if (!enrichedInvoiceData) return;
		queryClient.invalidateQueries({ queryKey: ALL_DRAFT_INVOICES_QUERY_KEY });
		queryClient.invalidateQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });
		if(enrichedInvoiceData.projectId){
			queryClient.invalidateQueries({ queryKey: PROJECTS_INVOICES_QUERY_KEY(enrichedInvoiceData.projectId) });
		}
		navigate(generatePath(INVOICE_DETAILS_PATH, { invoiceId: newInvoiceId }));
	}, [enrichedInvoiceData, navigate, queryClient]);

	const handleFinishRevertStatus = useCallback(async () => {
		if (!enrichedInvoiceData) return;
		await queryClient.refetchQueries({ queryKey: ALL_DRAFT_INVOICES_QUERY_KEY });
		await queryClient.refetchQueries({ queryKey: All_PENDING_INVOICES_QUERY_KEY });
		onClose();
		setShowRevertStatusForm(false);
	}, [enrichedInvoiceData, onClose, queryClient]);

	const dataReady = !!enrichedInvoiceData && allUsers && !options.isLoading;
	const sidebarReady = isOpen && dataReady;

	const showDetails = sidebarReady && !showFinishBillingProcessForm && !showRevertStatusForm;
	const finishInvoiceProcessFormVisible = sidebarReady && showFinishBillingProcessForm;
	const revertStStatusFormVisible = sidebarReady && showRevertStatusForm;
	return (
		<Sidebar closeOnOutsideClick={!showFinishBillingProcessForm}
				 open={isOpen}
				 setOpen={handleClose}>
			{options.isLoading && <SidebarBusyOverlay />}
			{showDetails && isProjectInvoice &&
				<DraftProjectInvoiceDetails allUsersData={allUsers}
											onClose={handleClose}
											onRevertStatusClick={() => setShowRevertStatusForm(true)}
											onStartBillingProcessClick={() => setShowFinishBillingProcessForm(true)}
											draftInvoiceData={enrichedInvoiceData}
											userCanManageInvoices={userCanManageInvoices}
				/>}
			{finishInvoiceProcessFormVisible && <FinishBillingProcessOfProjectInvoiceForm onSuccess={handleFinishSuccess}
																						  onCancel={() => setShowFinishBillingProcessForm(false)}
																						  draftInvoiceData={enrichedInvoiceData} />}
			{revertStStatusFormVisible && <RevertDraftProjectInvoiceStatusForm onCancel={() => setShowRevertStatusForm(false)}
																			   onSuccess={handleFinishRevertStatus}
																			   pendingInvoiceData={enrichedInvoiceData}
																			   allUsersData={allUsers} />}
		</Sidebar>
	);
};

export default DraftProjectInvoiceDetailsSidebar;
